import { Map, View } from 'ol';
import { wrapX } from 'ol/coordinate';
import { fromLonLat, get, toLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { type ShallowRef, shallowRef, ref, watch, computed } from 'vue';

import type { BasemapDefinition, LonLat, MapKitOptions } from '@/types';

import { useColorScheme } from './useColorScheme';

// MapTiler key — same as frontend-next
const MAPTILER_KEY = 'zOQth9GpecZYiskBLhVX';

// Mapbox token — same as frontend-next
const MAPBOX_TOKEN = 'pk.eyJ1IjoidGhvbWFzZ3J1ZWJsZXIiLCJhIjoiY2p6MmR1ZThmMDR3YTNibHRwd3czdDc2cCJ9.O_Oj1gj7Z-ySuJxdmLUuXA';

// HiDPI tile scale suffix
const tileScale = (typeof window !== 'undefined' && window.devicePixelRatio > 1) ? '@2x' : '';

/** Available basemap definitions — mirrors frontend-next base/index.ts */
const BASEMAPS: BasemapDefinition[] = [
  {
    id: 'maptilerTopo',
    name: 'Topographic',
    url: `https://api.maptiler.com/maps/9762bd38-2688-44ea-90da-0f31da1769d0/{z}/{x}/{y}${tileScale}.webp?key=${MAPTILER_KEY}`,
    attribution: '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    preferredScheme: 'light',
  },
  {
    id: 'satelliteHybrid',
    name: 'Satellite Hybrid',
    url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}${tileScale}?access_token=${MAPBOX_TOKEN}`,
    attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.maxar.com/">Maxar</a>',
  },
  {
    id: 'satellite',
    name: 'Satellite',
    url: `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}${tileScale}.webp?access_token=${MAPBOX_TOKEN}`,
    attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.maxar.com/">Maxar</a>',
  },
  {
    id: 'maptilerBright',
    name: 'Bright',
    url: `https://api.maptiler.com/maps/b29c0782-fa16-40c8-bd97-fc9392d74314/{z}/{x}/{y}${tileScale}.webp?key=${MAPTILER_KEY}`,
    attribution: '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    preferredScheme: 'light',
  },
];

const map = shallowRef<Map | null>(null);
let currentBaseLayer: TileLayer | null = null;
const activeBasemapId = ref<string>('maptilerTopo');
const manualBasemapOverride = ref(false);

/**
 * Core composable for creating and managing an OpenLayers map.
 *
 * Usage:
 * ```ts
 * const { map, initMap, setCenter, setZoom, getCenter } = useMap();
 * onMounted(() => { initMap('map-target'); });
 * ```
 */
export function useMap() {
  const { realColorScheme } = useColorScheme();

  const initMap = (
    targetId: string,
    options: MapKitOptions = {},
  ): ShallowRef<Map> => {
    const {
      center = [0, 20],
      zoom = 3,
      minZoom,
      maxZoom,
    } = options;

    const view = new View({
      center: fromLonLat(center),
      zoom,
      minZoom,
      maxZoom,
    });

    // Keep coordinates wrapped (prevent panning beyond ±180°)
    view.on('change:center', (evt) => {
      const c = evt.target.getCenter();
      const wrapped = wrapX(c, get('EPSG:3857')!);
      if (c[0] !== wrapped[0]) {
        evt.target.setCenter(wrapped);
      }
    });

    const m = new Map({
      target: targetId,
      view,
      controls: [],
    });

    map.value = m;
    applyBaseLayer();
    return map as ShallowRef<Map>;
  };

  /** Swap base tiles. When called without an id, picks the best basemap
   *  for the current color scheme (topo for light, darkmatter for dark). */
  const applyBaseLayer = (basemapId?: string) => {
    if (!map.value) return;

    const id = basemapId ?? activeBasemapId.value;
    const def = BASEMAPS.find((b) => b.id === id) ?? BASEMAPS[0];

    if (currentBaseLayer) {
      map.value.removeLayer(currentBaseLayer);
    }

    activeBasemapId.value = def.id;

    currentBaseLayer = new TileLayer({
      source: new XYZ({
        url: def.url,
        attributions: def.attribution,
        crossOrigin: 'anonymous',
        tilePixelRatio: window.devicePixelRatio > 1 ? 2 : 1,
        maxZoom: def.maxZoom ?? 20,
      }),
    });

    // Insert at index 0 so data layers stay on top
    map.value.getLayers().insertAt(0, currentBaseLayer);
  };

  /** Manually select a basemap by id — disables auto-switch on color scheme change. */
  const setBasemap = (id: string) => {
    manualBasemapOverride.value = true;
    applyBaseLayer(id);
  };

  // Re-apply base layer whenever the color scheme changes (unless user chose manually)
  watch(realColorScheme, (scheme) => {
    if (manualBasemapOverride.value) return;
    applyBaseLayer(scheme === 'dark' ? 'maptilerDark' : 'maptilerTopo');
  });

  const setCenter = (lonLat: LonLat, animationDuration = 250) => {
    map.value?.getView().animate({
      center: fromLonLat(lonLat),
      duration: animationDuration,
    });
  };

  const setZoom = (zoom: number, animationDuration = 250) => {
    map.value?.getView().animate({ zoom, duration: animationDuration });
  };

  const getCenter = (): LonLat | null => {
    const c = map.value?.getView().getCenter();
    if (!c) return null;
    return toLonLat(c) as LonLat;
  };

  const getZoom = (): number | undefined => {
    return map.value?.getView().getZoom();
  };

  const fitExtent = (
    extent: [number, number, number, number],
    padding = [50, 50, 50, 50],
  ) => {
    map.value?.getView().fit(extent, { padding, duration: 500 });
  };

  return {
    map,
    basemaps: BASEMAPS,
    activeBasemapId: computed(() => activeBasemapId.value),
    initMap,
    setCenter,
    setZoom,
    setBasemap,
    getCenter,
    getZoom,
    fitExtent,
    applyBaseLayer,
    MAPTILER_KEY,
  };
}
